import {
  Family,
  FamilyChildProfile,
  FamilyParentProfile,
} from "@joonapp/web-shared"
import { useMutation } from "@tanstack/react-query"

import { removeChild } from "./api"
import { QUERY_KEYS } from "../../constants"
import useFamilyUserSelectStore from "../../pages/family/hooks/useFamilyUserSelect"
import { queryClient } from "../../util/queryClient"
import {
  togglePause,
  toggleRoutineTimeLimits,
  updateGamePreference,
} from "../user"
import { editChild } from "../users/api"

export const useUpdatePreferenceMutation = () => {
  return useMutation({
    mutationFn: updateGamePreference,
    onMutate: async ({ key, childId }) => {
      // cancel the query to prevent it from updating with the optimistic response
      await queryClient.cancelQueries([QUERY_KEYS.FAMILY])
      // store the previous family data
      const previousFamily = queryClient.getQueryData([
        QUERY_KEYS.FAMILY,
      ]) as Family

      // either default being managed or the childId that is passed in
      const childIdToUpdate = childId
      // create new family data with the new preference
      const newFamily = {
        ...previousFamily,
        profiles: previousFamily?.profiles?.map(
          (profile: FamilyParentProfile | FamilyChildProfile) => {
            if (profile?.user?.id !== childIdToUpdate) return profile

            profile = profile as FamilyChildProfile
            return {
              ...profile,
              user: {
                ...profile.user,
                preferences: {
                  ...profile.user.preferences,
                  [key]: !profile.user?.preferences[key],
                },
              },
            }
          }
        ),
      }
      // update the query data with the new family data
      queryClient.setQueryData([QUERY_KEYS.FAMILY], newFamily)
      return { previousFamily }
    },
    // if there is an error, revert the query data to the previous family data
    onError: (_err, _family, context) => {
      queryClient.setQueryData([QUERY_KEYS.FAMILY], context?.previousFamily)
    },
    // invalidate the query to refetch the data
    onSettled: () => queryClient.invalidateQueries([QUERY_KEYS.FAMILY]),
  })
}

export const useTogglePauseMutation = () => {
  const { selectedUserId } = useFamilyUserSelectStore()

  return useMutation({
    mutationFn: togglePause,
    onMutate: async () => {
      await queryClient.cancelQueries([QUERY_KEYS.FAMILY])
      const previousFamily = queryClient.getQueryData([
        QUERY_KEYS.FAMILY,
      ]) as Family
      const newFamily = {
        ...previousFamily,
        profiles: previousFamily?.profiles?.map(
          (profile: FamilyParentProfile | FamilyChildProfile) => {
            if (profile?.user?.id !== selectedUserId) return profile
            profile = profile as FamilyChildProfile
            return {
              ...profile,
              user: {
                ...profile.user,
                active_doter: {
                  ...profile.user.active_doter,
                  is_paused: !profile.user.active_doter.is_paused,
                },
              },
            }
          }
        ),
      }
      queryClient.setQueryData([QUERY_KEYS.FAMILY], newFamily)
      return { previousFamily }
    },
    onError: (_err, _family, context) => {
      queryClient.setQueryData([QUERY_KEYS.FAMILY], context?.previousFamily)
    },
    onSettled: () => queryClient.invalidateQueries([QUERY_KEYS.FAMILY]),
  })
}

export const useToggleRoutineTimeLimitsMutation = () => {
  const { selectedUserId } = useFamilyUserSelectStore()

  return useMutation({
    mutationFn: toggleRoutineTimeLimits,
    onMutate: async ({ value }) => {
      await queryClient.cancelQueries([QUERY_KEYS.FAMILY])
      const previousFamily = queryClient.getQueryData([
        QUERY_KEYS.FAMILY,
      ]) as Family
      const newFamily = {
        ...previousFamily,
        profiles: previousFamily?.profiles?.map(
          (profile: FamilyParentProfile | FamilyChildProfile) => {
            if (profile?.user?.id !== selectedUserId) return profile
            profile = profile as FamilyChildProfile
            return {
              ...profile,
              user: {
                ...profile.user,
                preferences: {
                  ...profile.user.preferences,
                  afternoon_limit: value,
                  morning_limit: value,
                  night_limit: value,
                },
              },
            }
          }
        ),
      }
      queryClient.setQueryData([QUERY_KEYS.FAMILY], newFamily)
      return { previousFamily }
    },
    onError: (_err, _family, context) => {
      queryClient.setQueryData([QUERY_KEYS.FAMILY], context?.previousFamily)
    },
    onSettled: () => queryClient.invalidateQueries([QUERY_KEYS.FAMILY]),
  })
}

export const useEditChildMutation = () =>
  useMutation({
    mutationFn: editChild,
    onSuccess: async () =>
      await queryClient.invalidateQueries([QUERY_KEYS.FAMILY]),
  })

export const useRemoveChildMutation = () =>
  useMutation({
    mutationFn: removeChild,
    onSuccess: async () =>
      await queryClient.invalidateQueries([QUERY_KEYS.FAMILY]),
  })
